import { api } from "@/lib/api-client"

// =============================================================================
// Skill guidance API (backend: routes/admin/skills.py)
// =============================================================================

export type SkillGuidanceKey = "docx" | "pdf" | "pptx" | "xlsx"

export interface SkillGuidanceEntry {
  id: string
  title: string
  content: string
  enabled: boolean
  autoload: boolean
  updated_at?: string
  updated_by?: string | null
}

export type SkillGuidanceMap = Partial<Record<SkillGuidanceKey, Record<string, SkillGuidanceEntry>>>

export interface SkillGuidanceResponse {
  guidance: SkillGuidanceMap
  hash?: string | null
}

export async function getSkillGuidance(): Promise<SkillGuidanceResponse> {
  const response = await api.get("/admin/skills/guidance")
  return response.data || response
}

export async function replaceSkillGuidance(
  guidance: SkillGuidanceMap,
  options: { expectedHash: string; reason: string; source?: string }
): Promise<SkillGuidanceResponse & { success: boolean }> {
  const response = await api.put("/admin/skills/guidance", {
    guidance,
    expected_hash: options.expectedHash,
    reason: options.reason,
    source: options.source || "admin",
  })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to replace skill guidance")
  }

  return data
}

// =============================================================================
// Skill enable/disable switch (backend: routes/admin/skills.py)
// =============================================================================

export type SkillEnabledMap = Partial<Record<SkillGuidanceKey, boolean>>

export interface SkillEnabledResponse {
  enabled: SkillEnabledMap
  hash?: string | null
}

export async function getSkillEnabled(): Promise<SkillEnabledResponse> {
  const response = await api.get("/admin/skills/enabled")
  return response.data || response
}

export async function replaceSkillEnabled(
  enabled: SkillEnabledMap,
  options: { expectedHash: string; reason: string; source?: string }
): Promise<SkillEnabledResponse & { success: boolean }> {
  const response = await api.put("/admin/skills/enabled", {
    enabled,
    expected_hash: options.expectedHash,
    reason: options.reason,
    source: options.source || "admin",
  })
  const data = response.data || response

  if (data.success === false) {
    throw new Error(data.message || data.error || "Failed to update skill enabled flags")
  }

  return data
}

// =============================================================================
// Presentation template layout catalog (backend: routes/admin/presentation_templates.py)
// =============================================================================

export interface PresentationTemplateLayoutPlaceholder {
  idx: number | string | null
  type?: string | null
  name?: string | null
}

export interface PresentationTemplateLayout {
  layout_index: number
  layout_name: string
  semantic_role: string
  description?: string | null
  placeholders: PresentationTemplateLayoutPlaceholder[]
  preview_thumbnail_base64?: string | null
  analysis_status?: string | null
  master_index?: number | null
  layout_in_master_index?: number | null
  keywords?: string[]
}

export interface PresentationTemplateCatalogResponse {
  template_filename: string | null
  template_sha256: string | null
  layouts: PresentationTemplateLayout[]
  analyzed: boolean
}

export interface PresentationTemplateAnalysisResponse {
  template_filename: string
  template_sha256: string
  layout_count: number
  layouts: PresentationTemplateLayout[]
}

export async function getPresentationTemplateCatalog(): Promise<PresentationTemplateCatalogResponse> {
  const response = await api.get("/admin/presentation-templates/catalog")
  return response.data || response
}

export type PresentationBrandAssetBlob = {
  blob_name?: string
  content_type?: string
  kind?: string
  size_bytes?: number
  width?: number
  height?: number
  version?: string
  uploaded_at?: string
}

export type PresentationBrandAssetSlot = {
  filename: string
  required: boolean
  role: "frame_logo" | "content_logo" | "other" | string
  blob?: PresentationBrandAssetBlob | null
}

export type PresentationBrandResponse = {
  enabled: boolean
  spec_filename: string | null
  spec_markdown: string
  assets: Record<string, PresentationBrandAssetSlot>
  enforce_company_template: boolean
  generation_mode: "markdown" | "managed" | "ask"
  detected_filenames: string[]
  bound: boolean
  complete: boolean
  missing_required: string[]
  hash: string
  maxSpecMarkdownChars: number
  maxUploadBytes: number
  success?: boolean
}

export async function getPresentationBrand(): Promise<PresentationBrandResponse> {
  const response = await api.get("/admin/presentation-brand")
  return response.data || response
}

export async function putPresentationBrand(payload: {
  enabled: boolean
  spec_filename: string | null
  spec_markdown: string
  enforce_company_template?: boolean
  generation_mode?: "markdown" | "managed" | "ask"
  expectedHash: string
  reason: string
}): Promise<PresentationBrandResponse> {
  const body: Record<string, unknown> = {
    enabled: payload.enabled,
    spec_filename: payload.spec_filename,
    spec_markdown: payload.spec_markdown,
    expected_hash: payload.expectedHash,
    reason: payload.reason,
  }
  if (payload.enforce_company_template !== undefined) {
    body.enforce_company_template = payload.enforce_company_template
  }
  if (payload.generation_mode !== undefined) {
    body.generation_mode = payload.generation_mode
  }
  const response = await api.put("/admin/presentation-brand", body)
  const data = response.data || response
  if (data.error) {
    throw new Error(String(data.error))
  }
  return data
}

export async function uploadPresentationBrandAsset(
  slotId: string,
  file: File | undefined,
  meta: { filename: string; required: boolean; role: string }
): Promise<PresentationBrandResponse> {
  const form = new FormData()
  if (file) {
    form.append("file", file)
  }
  form.append("filename", meta.filename)
  form.append("required", meta.required ? "true" : "false")
  form.append("role", meta.role)
  const response = await api.post(
    `/admin/presentation-brand/assets/${encodeURIComponent(slotId)}`,
    form
  )
  const data = response.data || response
  if (data.error) {
    throw new Error(String(data.error))
  }
  return data
}

export async function getPresentationBrandAssetContent(slotId: string): Promise<Blob> {
  const response = await api.get(
    `/admin/presentation-brand/assets/${encodeURIComponent(slotId)}/content`,
    { responseType: "blob" }
  )
  return response.data as Blob
}

export async function deletePresentationBrandAsset(
  slotId: string
): Promise<PresentationBrandResponse> {
  const response = await api.delete(
    `/admin/presentation-brand/assets/${encodeURIComponent(slotId)}`
  )
  const data = response.data || response
  if (data.error) {
    throw new Error(String(data.error))
  }
  return data
}

export async function analyzePresentationTemplate(): Promise<PresentationTemplateAnalysisResponse> {
  try {
    const response = await api.post("/admin/presentation-templates/analyze", {})
    const data = response.data || response
    if (data.error) {
      throw new Error(String(data.error))
    }
    return data
  } catch (err) {
    // Axios throws on non-2xx; pull the server-supplied error message out
    // of the response body so the UI shows the real reason instead of the
    // generic "Request failed with status code 500".
    const responseData = (err as { response?: { data?: { error?: string } } }).response?.data
    if (responseData?.error) {
      throw new Error(responseData.error)
    }
    throw err
  }
}
